import React, { useRef, useState } from "react";
import { t } from "ttag";

import { useAppDispatch, useAppSelector } from "../../../apps/reducers";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { ILocation } from "../../../models/location";
import { isoLatitude, isoLongitude } from "../../../models/nominals";
import { geocode, loadGMaps, newAutocomplete } from "../../../utils/maps";
import { updateEnteredLocation } from "../reducers";
import {
    getFormattedAddressForPlace,
    getPostCodeForPlace,
    preferredLocationSelector,
} from "../selectors";

const getInitialSearchText = (
    currentLocation: ILocation | null,
    inputFormat: "short" | "long",
): string => {
    if (!currentLocation) {
        return "";
    }
    if (inputFormat === "long" && currentLocation.formatted_address_long) {
        return currentLocation.formatted_address_long;
    }
    return currentLocation.formatted_address;
};

const getCountriesForRestriction = (country?: string): string[] => {
    switch (country) {
        case "MX":
            return ["mx"];
        default:
            return ["us", "bs", "ky", "pr", "bm"];
    }
};

const updateLocationWithPlaceData = async (
    place: google.maps.places.PlaceResult | google.maps.GeocoderResult,
    searchText: string,
    onUpdateEnteredLocation: (location: ILocation) => void,
    setSearchTextAtTimeOfLastUpdate: (text: string) => void,
    updateLocationWithSearchText: (text: string) => Promise<boolean>,
): Promise<boolean> => {
    if (!place.geometry || !place.geometry.location) {
        console.log(
            "Can not submit form with place data because place has no geometry info",
        );
        const placeName = (place as google.maps.places.PlaceResult).name;
        if (placeName) {
            return updateLocationWithSearchText(placeName);
        }
        return false;
    }
    setSearchTextAtTimeOfLastUpdate(searchText);
    onUpdateEnteredLocation({
        formatted_address: getFormattedAddressForPlace(place),
        formatted_address_long: getFormattedAddressForPlace(place, true),
        lat: isoLatitude.wrap(place.geometry.location.lat()),
        lng: isoLongitude.wrap(place.geometry.location.lng()),
        zip: getPostCodeForPlace(place) || undefined,
    });
    return true;
};

const updateLocationWithSearchTextImpl = async (
    searchText: string,
    onUpdateEnteredLocation: (location: ILocation) => void,
    setSearchTextAtTimeOfLastUpdate: (text: string) => void,
    updateLocationWithSearchText: (text: string) => Promise<boolean>,
): Promise<boolean> => {
    const results = await geocode({
        address: searchText,
    });
    if (results.length <= 0) {
        console.log("Geocoding request returned no results");
        return false;
    }
    return updateLocationWithPlaceData(
        results[0],
        searchText,
        onUpdateEnteredLocation,
        setSearchTextAtTimeOfLastUpdate,
        updateLocationWithSearchText,
    );
};

export const LocationInput = (props: {
    inputProps: React.DetailedHTMLProps<
        React.InputHTMLAttributes<HTMLInputElement>,
        HTMLInputElement
    >;
    inputFormat: "short" | "long";
    country?: string;
    onAfterChange?: (searchText: string) => void;
}): React.ReactNode => {
    const currentLocation = useAppSelector(preferredLocationSelector);
    const dispatch = useAppDispatch();

    const [searchText, setSearchText] = useState(() =>
        getInitialSearchText(currentLocation, props.inputFormat),
    );
    const [
        searchTextAtTimeOfLastLocationUpdate,
        setSearchTextAtTimeOfLastUpdate,
    ] = useState("");

    const autocompleteElem = useRef<HTMLInputElement>(null);
    const autocompleteObj = useRef<google.maps.places.Autocomplete | undefined>(
        undefined,
    );

    const updateLocationWithSearchText = async (
        text: string,
    ): Promise<boolean> => {
        return updateLocationWithSearchTextImpl(
            text,
            (location) => dispatch(updateEnteredLocation(location)),
            setSearchTextAtTimeOfLastUpdate,
            updateLocationWithSearchText,
        );
    };

    const onTextChange = (event: React.FormEvent<HTMLInputElement>) => {
        setSearchText(event.currentTarget.value);
    };

    const onBlur = () => {
        // If the field loses focus, but the search text has changed since we
        // last updated the location, update the location using the search text.
        if (searchTextAtTimeOfLastLocationUpdate !== searchText) {
            void updateLocationWithSearchText(searchText);
        }
    };

    const onAutocompletePlaceChange = async () => {
        if (!autocompleteObj.current) {
            return;
        }
        const place = autocompleteObj.current.getPlace();
        const newSearchText = getFormattedAddressForPlace(
            place,
            props.inputFormat === "long",
        );
        setSearchText(newSearchText);
        const success = await updateLocationWithPlaceData(
            place,
            newSearchText,
            (location) => dispatch(updateEnteredLocation(location)),
            setSearchTextAtTimeOfLastUpdate,
            updateLocationWithSearchText,
        );
        if (success && props.onAfterChange) {
            props.onAfterChange(newSearchText);
        }
    };

    useMountEffect(() => {
        const initializeAutocomplete = async () => {
            await loadGMaps();
            if (!autocompleteElem.current) {
                return;
            }
            const countries = getCountriesForRestriction(props.country);
            autocompleteObj.current = newAutocomplete(
                autocompleteElem.current,
                {
                    componentRestrictions: {
                        country: countries,
                    },
                },
            );
            autocompleteObj.current.addListener(
                "place_changed",
                onAutocompletePlaceChange,
            );
        };

        void initializeAutocomplete();
    });

    return (
        <>
            <input
                {...props.inputProps}
                // TODO(i18n): Use t tagged template for aria-label: aria-label={t`enter your location`}
                aria-label="enter your location"
                type="text"
                autoComplete="off"
                ref={autocompleteElem}
                onChange={onTextChange}
                onBlur={onBlur}
                value={searchText}
                aria-describedby="retail-locator-location_describedby"
            />
            <span
                className="ada-screenreader-only"
                id="retail-locator-location_describedby"
            >
                {t`Begin typing to search, use arrow keys to navigate, Enter to select`}
            </span>
        </>
    );
};
